// FAQ — setup walkthroughs (CRM sync, proptrees, forms), accessible from
// the profile dropdown.
import { FileText, HelpCircle, ListTree, Plug, Power, Users } from 'lucide-react';
import { AppShell } from '../components/layout/AppShell.js';
import { HowToVideo, VIDEOS } from '../components/HowToVideo.js';
import { SectionCard } from '../components/SectionCard.js';

export function FaqPage() {
  return (
    <AppShell>
      <div className="mx-auto max-w-[860px] space-y-6 pb-10">
        <div>
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            FAQ
          </h1>
          <p className="mt-3 font-mono text-[11px] text-muted-foreground">
            How Proptree works, in one place.
          </p>
        </div>

        <div className="space-y-4">
          <SectionCard
            title="Do I have to sync a CRM?"
            subtitle="Proptree works with or without CRM sync"
            icon={Power}
            defaultOpen
          >
            <Faq>
              <p>
                No. Syncing keeps your listings and agents up to date
                automatically, but you can run Proptree without it.
              </p>
              <Bullets
                items={[
                  'Turn sync on or off anytime under CRM settings → "Sync a CRM with Proptree".',
                  'While sync is off, the CRM connection and lead management settings are grayed out and the Agents, Listings, and Proptree tabs are hidden.',
                  'Nothing is ever deleted — switch sync back on and everything reappears exactly as it was.',
                ]}
              />
            </Faq>
          </SectionCard>

          <SectionCard
            title="How do I connect my CRM?"
            subtitle="Feed URL or API keys — about a minute"
            icon={Plug}
          >
            <Faq>
              <HowToVideo src={VIDEOS.crmConnect} />
              <Bullets
                items={[
                  'Open CRM settings — the highlighted button at the right end of the main menu.',
                  'Under CRM connection, pick your CRM (PropSpace, Pixxi, GoYzer, Property Finder, or something else).',
                  'Paste your feed URL — or API key and secret for Property Finder — and save.',
                  'Your listings and agents sync in automatically from then on.',
                ]}
              />
            </Faq>
          </SectionCard>

          <SectionCard
            title="Where is my agency proptree?"
            subtitle="Your brokerage's public link-in-bio page"
            icon={ListTree}
          >
            <Faq>
              <HowToVideo src={VIDEOS.agencyProptree} />
              <p>
                Your agency proptree is one link that always shows your live
                listings and brand.
              </p>
              <Bullets
                items={[
                  'Open the Proptree tab in the main menu and choose Agency proptree.',
                  'Edit the alias, branding, buttons, and banner — the live preview on the right updates as you type.',
                  'Share the proptr.ee link anywhere: Instagram bio, WhatsApp, email signatures.',
                ]}
              />
            </Faq>
          </SectionCard>

          <SectionCard
            title="Where are agent proptrees?"
            subtitle="A personal page for every agent"
            icon={Users}
          >
            <Faq>
              <HowToVideo src={VIDEOS.agentProptrees} />
              <Bullets
                items={[
                  'Find them under the Proptree tab → Agent proptrees.',
                  'Each row shows the agent’s page status, visits, and inquiries.',
                  'Agents share their personal link; every inquiry lands in your Inquiries list automatically.',
                ]}
              />
            </Faq>
          </SectionCard>

          <SectionCard
            title="How do forms work?"
            subtitle="Qualify leads before they reach your agents"
            icon={FileText}
          >
            <Faq>
              <HowToVideo src={VIDEOS.forms} />
              <p>
                Forms qualify your leads — contact requests, viewings,
                callback bookings — before they reach your agents.
              </p>
              <Bullets
                items={[
                  'A default inquiry form already exists for your brokerage — you can use it as-is.',
                  'Open the Forms tab to edit the default form or create new ones.',
                  'Attach a form to a proptree and every submission shows up under Inquiries with the answers.',
                ]}
              />
            </Faq>
          </SectionCard>

          <SectionCard
            title="Still stuck?"
            subtitle="We're happy to help"
            icon={HelpCircle}
          >
            <Faq>
              <p>
                Reach out to the Proptree team and we'll walk you through it —
                use the contact details on proptr.ee, or ask the person who set
                up your brokerage.
              </p>
            </Faq>
          </SectionCard>
        </div>
      </div>
    </AppShell>
  );
}

function Faq({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-3 p-5 font-sans text-[13px] leading-relaxed text-muted-foreground">
      {children}
    </div>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5">
      {items.map((b) => (
        <li key={b} className="flex items-start gap-2">
          <span
            aria-hidden
            className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60"
          />
          <span className="text-foreground/90">{b}</span>
        </li>
      ))}
    </ul>
  );
}
